import { useCallback } from "react";

import useRiskFinancingData from "../../../hooks/risk_financing/useRiskFinancingData";
import useScenarioFinancing from "../../../hooks/risk_financing/useScenarioFinancing";
import useCoveragePortfolio from "../../../hooks/risk_financing/useCoveragePortfolio";
import useActiveFairScenarios from "../../../hooks/risk_financing/useActiveFairScenarios";
import useCoverageAdequacy from "../../../hooks/risk_financing/useCoverageAdequacy";
import usePortfolioRiskAnalysis from "../../../hooks/risk_financing/usePortfolioRiskAnalysis";


export default function useRiskFinancingEngine({
  activeTab,
  settingsLoaded,
  simulations,
  strategyAnalysisEnabled = true,
  onTabChange,
} = {}) {

  /*
   * Portfolio financing and decision-analysis engine.
   */
  const {
    strategy,
    portfolioData,
    decisionData,

    initialLoading,
    portfolioLoading,
    decisionLoading,

    portfolioError,
    decisionError,

    handleStrategyChange,
    recalculatePortfolio,
  } = useRiskFinancingData({
    settingsLoaded,
    simulations,
    strategyAnalysisEnabled,
  });

  /*
   * Scenario-level financing engine.
   */
  const {
    scenarios,
    selectedScenarioId,
    strategy: scenarioStrategy,
    scenarioData,

    scenariosLoading,
    analysisLoading,

    scenariosError,
    analysisError,

    setSelectedScenarioId,
    handleStrategyChange:
      handleScenarioStrategyChange,
    analyzeScenario,
  } = useScenarioFinancing({
    enabled:
      settingsLoaded &&
      activeTab === "scenario",

    simulations,
  });

  /*
   * Insurance coverage portfolio.
   */
  const {
    coverages,
    loading: coverageLoading,
    mutationLoading:
      coverageMutationLoading,
    error: coverageError,

    createCoverage,
    updateCoverage,
    changeCoverageStatus,
    deleteCoverage,

    linkScenario,
    unlinkScenario,
  } = useCoveragePortfolio({
    enabled:
      settingsLoaded &&
      activeTab === "coverage",
  });

  /*
   * Active FAIR scenarios used by the
   * coverage portfolio.
   */
  const {
    scenarios: activeFairScenarios,
    loading: activeScenariosLoading,
    error: activeScenariosError,
  } = useActiveFairScenarios({
    enabled:
      settingsLoaded &&
      activeTab === "coverage",
  });

  /*
   * Coverage adequacy analysis.
   */
  const {
        data: coverageAdequacyData,
        loading: coverageAdequacyLoading,
        error: coverageAdequacyError,
        refresh: refreshCoverageAdequacy,
        } = useCoverageAdequacy({
        enabled: settingsLoaded,

        simulations,
  });

  const {
    data: portfolioRiskAnalysis,
    loading: portfolioRiskAnalysisLoading,
    error: portfolioRiskAnalysisError,
  } = usePortfolioRiskAnalysis({
    enabled: settingsLoaded,
    simulations,
  });


  const handleAnalyzeCoverageScenario =
    useCallback(
      (scenarioId) => {
        setSelectedScenarioId(
          String(scenarioId)
        );

        if (
          typeof onTabChange ===
          "function"
        ) {
          onTabChange("scenario");
        }
      },
      [
        onTabChange,
        setSelectedScenarioId,
      ]
    );

  const handleCompareScenarioAlternatives =
    useCallback(() => {
      if (
        typeof onTabChange ===
        "function"
      ) {
        onTabChange(
          "decision_analysis"
        );
      }
    }, [onTabChange]);

    // funciones, handlers, useEffects, etc.

  /*
   * Structured engine output.
   *
   * The page can consume these sections,
   * while Decision Center adapters can
   * later consume the same object.
   */
  return {
    /*
    * Structured API
    *
    * This is the target interface for new
    * components and Decision Center adapters.
    * 
    */

    engineId: "risk_financing",

    version: 1,

    portfolio: {
        /*
        * Domain data
        */
        strategy,

        summary:
            portfolioData?.summary || {},

        kpis:
            portfolioData?.kpis || {},

        curve:
            Array.isArray(portfolioData?.curve)
                ? portfolioData.curve
                : [],

        recommendations:
            Array.isArray(
                portfolioData?.recommendations
            )
                ? portfolioData.recommendations
                : [],

        /*
        * Complete API response
        */
        data:
            portfolioData || null,

        /*
        * Engine state
        */
        state: {
            loading:
                portfolioLoading,

            error:
                portfolioError,

            available:
                Boolean(portfolioData),
        },

        actions: {
            changeStrategy:
                handleStrategyChange,

            recalculate:
                recalculatePortfolio,
        },
    },

    scenario: {
        /*
        * Domain data
        */
        list:
            Array.isArray(scenarios)
                ? scenarios
                : [],

        selectedId:
            selectedScenarioId,

        selected:
            Array.isArray(scenarios)
                ? scenarios.find(
                    (scenario) =>
                        String(scenario?.id) ===
                        String(selectedScenarioId)
                ) || null
                : null,

        strategy:
            scenarioStrategy,

        analysis:
            scenarioData || null,

        alternatives:
            Array.isArray(
                scenarioData?.alternatives
            )
                ? scenarioData.alternatives
                : [],

        recommendations:
            Array.isArray(
                scenarioData?.recommendations
            )
                ? scenarioData.recommendations
                : [],

        /*
        * Engine state
        */
        state: {
            loading:
                scenariosLoading ||
                analysisLoading,

            listLoading:
                scenariosLoading,

            analysisLoading,

            error:
                scenariosError ||
                analysisError,

            listError:
                scenariosError,

            analysisError,

            available:
                Array.isArray(scenarios) &&
                scenarios.length > 0,
        },

        actions: {
            select:
                setSelectedScenarioId,

            changeStrategy:
                handleScenarioStrategyChange,

            analyze:
                analyzeScenario,

            compareAlternatives:
                handleCompareScenarioAlternatives,
        },
    },

    appetite: {
        analysis: portfolioRiskAnalysis,
        state: {
            loading: portfolioRiskAnalysisLoading,
            error: portfolioRiskAnalysisError,
            available: Boolean(portfolioRiskAnalysis?.appetite),
        },
    },

    coverage: {
        /*
        * Domain data
        */
        portfolio:
            Array.isArray(coverages)
                ? coverages
                : [],

        scenarios:
            Array.isArray(activeFairScenarios)
                ? activeFairScenarios
                : [],

        adequacy:
            coverageAdequacyData || null,

        summary:
            coverageAdequacyData?.summary || {},

        active:
            Array.isArray(coverages)
                ? coverages.filter((coverage) => {
                    const status = String(
                        coverage?.status || ""
                    )
                        .trim()
                        .toLowerCase();

                    return (
                        status === "active" ||
                        status === "in_force" ||
                        status === "in force"
                    );
                })
                : [],

        expiring:
            Array.isArray(coverages)
                ? coverages.filter((coverage) => {
                    const status = String(
                        coverage?.status || ""
                    )
                        .trim()
                        .toLowerCase();

                    return (
                        status === "expiring" ||
                        status === "expiring_soon" ||
                        status === "expiring soon"
                    );
                })
                : [],

        /*
        * Engine state
        */
        state: {
            loading:
                coverageLoading ||
                activeScenariosLoading ||
                coverageAdequacyLoading,

            portfolioLoading:
                coverageLoading,

            scenariosLoading:
                activeScenariosLoading,

            adequacyLoading:
                coverageAdequacyLoading,

            mutationLoading:
                coverageMutationLoading,

            error:
                coverageError ||
                activeScenariosError ||
                coverageAdequacyError,

            portfolioError:
                coverageError,

            scenariosError:
                activeScenariosError,

            adequacyError:
                coverageAdequacyError,

            available:
                Array.isArray(coverages) &&
                coverages.length > 0,
        },

        actions: {
            create:
                createCoverage,

            update:
                updateCoverage,

            changeStatus:
                changeCoverageStatus,

            remove:
                deleteCoverage,

            linkScenario,

            unlinkScenario,

            analyzeScenario:
                handleAnalyzeCoverageScenario,

            refreshAdequacy:
                refreshCoverageAdequacy,
        },
    },
        
    decisionAnalysis: {
        analysis:
            decisionData || null,

        recommendations:
            Array.isArray(
                decisionData?.recommendations
            )
                ? decisionData.recommendations
                : [],

        alternatives:
            Array.isArray(
                decisionData?.alternatives
            )
                ? decisionData.alternatives
                : Array.isArray(
                    decisionData?.strategies
                )
                    ? decisionData.strategies
                    : [],

        selectedAlternative:
            decisionData?.selected_alternative ||
            decisionData?.selectedAlternative ||
            null,

        state: {
            loading:
                decisionLoading,

            error:
                decisionError,

            available:
                Boolean(decisionData),
        },
    },

    status: {
        /*
        * Global engine state
        */
        phase:
            initialLoading
            ? "initializing"
            : (
                portfolioLoading ||
                decisionLoading ||
                scenariosLoading ||
                analysisLoading ||
                coverageLoading ||
                activeScenariosLoading ||
                coverageAdequacyLoading ||
                portfolioRiskAnalysisLoading
                )
                ? "loading"
                : (
                    portfolioError ||
                    decisionError ||
                    scenariosError ||
                    analysisError ||
                    coverageError ||
                    activeScenariosError ||
                    coverageAdequacyError ||
                    portfolioRiskAnalysisError
                )
                ? "error"
                : "ready",

        initialLoading,

        isLoading:
            initialLoading ||
            portfolioLoading ||
            decisionLoading ||
            scenariosLoading ||
            analysisLoading ||
            coverageLoading ||
            activeScenariosLoading ||
            coverageAdequacyLoading ||
            portfolioRiskAnalysisLoading,

        hasError:
            Boolean(
            portfolioError ||
            decisionError ||
            scenariosError ||
            analysisError ||
            coverageError ||
            activeScenariosError ||
            coverageAdequacyError ||
            portfolioRiskAnalysisError
            ),

        errors: {
            portfolio:
            portfolioError || null,

            decisionAnalysis:
            decisionError || null,

            scenarioList:
            scenariosError || null,

            scenarioAnalysis:
            analysisError || null,

            coveragePortfolio:
            coverageError || null,

            coverageScenarios:
            activeScenariosError || null,

            coverageAdequacy:
            coverageAdequacyError || null,

            portfolioRiskAnalysis:
            portfolioRiskAnalysisError || null,
        },

        loading: {
            portfolio:
            Boolean(portfolioLoading),

            decisionAnalysis:
            Boolean(decisionLoading),

            scenarioList:
            Boolean(scenariosLoading),

            scenarioAnalysis:
            Boolean(analysisLoading),

            coveragePortfolio:
            Boolean(coverageLoading),

            coverageScenarios:
            Boolean(activeScenariosLoading),

            coverageAdequacy:
            Boolean(coverageAdequacyLoading),

            portfolioRiskAnalysis:
            Boolean(portfolioRiskAnalysisLoading),
        },

        available: {
            portfolio:
            Boolean(portfolioData),

            decisionAnalysis:
            Boolean(decisionData),

            scenarios:
            Array.isArray(scenarios) &&
            scenarios.length > 0,

            coveragePortfolio:
            Array.isArray(coverages) &&
            coverages.length > 0,

            coverageAdequacy:
            Boolean(coverageAdequacyData),

            portfolioRiskAnalysis:
            Boolean(portfolioRiskAnalysis?.appetite),
        },
    },

    raw: {
        portfolioData,
        decisionData,
        scenarioData,
        coverageAdequacyData,
        portfolioRiskAnalysis,
        coverages,
    },

    metadata: {

        generatedAt: new Date().toISOString(),

        activeTab,

        simulations,

    },


  };
}
